<template>
    <div class="content">
        <v-form ref="setupForm" v-if="!useConfluenceExperimentSetup">
            <v-card>
                <v-card-title primary-title> Experiment Setup </v-card-title>
                <v-card-text>
                    <v-container
                        class="text-center"
                        v-if="createdExperiments.length > 0"
                        grid-list-md
                    >
                        <v-row>
                            <v-col cols="12">
                                <h3>
                                    Attempting to create
                                    {{ formData.numberOfExperiments }} experiment(s).
                                </h3>
                                <p>
                                    Please do not navigate away from this page until all experiments
                                    have been created.
                                </p>
                            </v-col>
                        </v-row>
                        <v-row v-for="(experiment, index) in createdExperiments" :key="index">
                            <v-col cols="12">
                                <a :href="experiment.webURL" target="_blank">{{
                                    experiment.experimentId
                                }}</a>
                                has been submitted.
                                <v-alert v-if="experiment.setupStatus === 'Pending'" type="warning">
                                    Experiment setup completion is pending background processes for
                                    experiment {{ index + 1 }} / {{ formData.numberOfExperiments }}.
                                </v-alert>
                                <v-alert
                                    v-else-if="experiment.setupStatus === 'Completed'"
                                    type="success"
                                >
                                    Experiment setup has completed successfully for experiment
                                    {{ index + 1 }} / {{ formData.numberOfExperiments }}.
                                </v-alert>
                                <v-alert
                                    v-else-if="experiment.setupStatus === 'Failed'"
                                    type="error"
                                >
                                    Experiment setup failed with background error(s)
                                    {{
                                        experiment.setupErrors
                                            ? experiment.setupErrors.join(", ")
                                            : ""
                                    }}
                                </v-alert>
                            </v-col>
                        </v-row>
                        <v-row v-if="allCompletedSuccessfully && formData.numberOfExperiments > 1">
                            <v-col cols="12">
                                <v-alert type="success">
                                    All experiments have been created successfully.
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-container>

                    <v-container class="text-center" v-else grid-list-md>
                        <v-row>
                            <v-col md="2">
                                <v-select
                                    v-model="formData.numberOfExperiments"
                                    :items="[1, 2, 3, 4, 5]"
                                    label="Number of Experiments (*)"
                                >
                                </v-select>
                            </v-col>
                            <v-col md="3" v-if="isAuthorized('Experiments:UseConfluenceSetup')">
                                <v-checkbox
                                    v-model="useConfluenceExperimentSetup"
                                    label="Use Confluence"
                                ></v-checkbox>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    label="Name (*)"
                                    v-model="formData.name"
                                    :rules="requiredTextValueRules('Experiment name')"
                                    required
                                ></v-text-field>
                            </v-col>
                            <v-col md="4">
                                <ProjectFolderSelect v-model="selectedProjectFolder" />
                            </v-col>
                            <v-col md="4">
                                <v-date-input
                                    v-model="formData.start_date"
                                    label="Day 0 (*)"
                                    :rules="requiredTextValueRules('Day 0')"
                                    required
                                ></v-date-input>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" sm="4" md="4">
                                <ProtocolSelect
                                    v-model="selectedProtocol"
                                    v-on:update:model-value="refreshProtocolFields"
                                    :protocols="protocols"
                                    :project-folder-option="selectedProjectFolder"
                                />
                            </v-col>
                            <v-col md="4">
                                <v-autocomplete
                                    v-model="objective"
                                    :items="experimentObjectives"
                                    label="Objective"
                                    clearable
                                />
                            </v-col>
                            <v-col md="4">
                                <v-autocomplete
                                    :items="filteredExperimentGroups"
                                    label="Experiment Group (*)"
                                    clearable
                                    v-model="selectedExperimentGroupId"
                                    item-title="name"
                                    item-value="id"
                                    :rules="requiredSimpleValueRules('Experiment group')"
                                >
                                </v-autocomplete>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" md="4">
                                <v-autocomplete
                                    :items="containerTypes"
                                    label="Container Type"
                                    clearable
                                    v-model="formData.container_type"
                                    return-object
                                    item-title="name"
                                    item-value="id"
                                    :rules="requiredSimpleValueRules('Container type')"
                                >
                                </v-autocomplete>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <experiment-metadata-template-fields
                                    class="ml-n4"
                                    metadata-context="experiment"
                                    v-model="experimentMetadata"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col md="4">
                                <v-file-input
                                    v-model="file"
                                    accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    label="Choose an excel file"
                                    :rules="requiredFileRules('File')"
                                >
                                </v-file-input>
                                <div class="pt-3">
                                    <a
                                        href="javascript:void(0)"
                                        @click="createSetupTemplateExcel()"
                                        style="
                                            cursor: pointer;
                                            text-decoration: underline;
                                            color: blue;
                                        "
                                    >
                                        Download Setup Template
                                    </a>
                                </div>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions v-if="createdExperiments.length < 1">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="cancelIt"> Cancel </v-btn>
                    <v-btn color="primary" @click="saveIt"> Save </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
        <experiment-setup-v2 v-else @exitConfluenceSetup="confluenceSetupExited" />
    </div>
</template>

<script>
import { defineComponent } from "vue";

import _ from "lodash";
import config from "../../config";
import axios from "../../lib/apiAxios";
import { EventBus } from "@/lib/event-bus";
import { mapGetters } from "vuex";
import {
    benchlingAPI,
    fetchContainerTypes,
    fetchProtocols,
} from "@/services/benchling/benchlingAPI";
import { experimentGroupsAPI } from "@/services/experimentGroups/experimentGroupsAPI";
import ProjectFolderSelect from "@/components/Experiments/shared/ProjectFolderSelect.vue";
import ProtocolSelect from "@/components/Experiments/shared/ProtocolSelect.vue";
import {
    requiredFileRules,
    requiredSimpleValueRules,
    requiredTextValueRules,
} from "@/lib/validationUtils.js";
import ExperimentSetupV2 from "@/components/Experiments/ExperimentSetupV2.vue";
import ExperimentMetadataTemplateFields from "@/components/Experiments/ExperimentMetadataTemplateFields.vue";
import { buildExperimentObjectivesOptions } from "@/lib/experimentMetadata";

class ExperimentSetupError extends Error {}

export default defineComponent({
    name: "experiment-setup",
    components: {
        ExperimentSetupV2,
        ProtocolSelect,
        ProjectFolderSelect,
        ExperimentMetadataTemplateFields,
    },
    props: [],

    async created() {
        this.protocols = await fetchProtocols();
        const allExperimentGroups = await experimentGroupsAPI.fetchExperimentGroups();
        this.experimentGroups = allExperimentGroups.filter(
            (experimentGroup) => experimentGroup.isAvailableInExperimentSetup
        );
        this.containerTypes = await fetchContainerTypes();
        this.experimentObjectives = buildExperimentObjectivesOptions(this.experimentGroups);
    },

    computed: {
        ...mapGetters(["isAuthorized"]),
        // Determines if the user has overridden the Container Type from the Protocol
        containerTypeOverride() {
            return _.get(this.formData, "assay_type") &&
                _.get(this.formData, "container_type") &&
                this.formData.assay_type.container_type !== this.formData.container_type.id
                ? this.formData.container_type
                : null;
        },
        allCompletedSuccessfully() {
            return (
                this.createdExperiments.length === this.formData.numberOfExperiments &&
                this.createdExperiments.every(
                    (experiment) => experiment.setupStatus === "Completed"
                )
            );
        },
        filteredExperimentGroups() {
            if (!this.objective) {
                return this.experimentGroups;
            }
            if (this.objective === "None") {
                return this.experimentGroups.filter((group) => group.objective === null);
            }
            return this.experimentGroups.filter((group) => group.objective === this.objective);
        },
    },

    methods: {
        createSetupTemplateExcel() {
            window.open(
                `${config.apiHost}/experiment/excel/experimentSetup/expSetupTemplate.xlsx`,
                "_blank"
            );
        },
        refreshProtocolFields: function (protocol) {
            if (protocol && protocol.container_type) {
                this.formData.container_type = _.find(this.containerTypes, {
                    id: protocol.container_type,
                });
            } else {
                this.formData.container_type = null;
            }
        },
        cancelIt() {
            this.$router.push({ path: "/experiments" });
        },
        async saveIt() {
            this.saving = true;
            // Check there are no validation errors
            if (!(await this.validate())) {
                return;
            }

            // Show loading screen
            EventBus.$emit("globalLoadScreen", { status: true, message: "Creating Experiment" });
            // Store metadata in formData
            this.formData.projectId = this.selectedProjectFolder.projectId;
            this.formData.folderId = this.selectedProjectFolder.folderId;
            this.formData.assay_type = this.selectedProtocol;
            this.formData.experimentMetadata = this.experimentMetadata;
            this.formData.experimentGroupId = this.selectedExperimentGroupId;

            // Check for Container Type override
            if (this.containerTypeOverride) {
                console.log(
                    "The Container Type was overridden by the user to: ",
                    this.containerTypeOverride
                );
                this.formData.container_type = this.containerTypeOverride;
                this.formData.container_type.override = true;
            }

            console.log(
                `Attempting to create ${this.formData.numberOfExperiments} experiments with the following data:`,
                this.formData
            );

            for (let i = 0; i < this.formData.numberOfExperiments; i++) {
                const experimentName =
                    i > 0
                        ? this.constructExperimentName(this.formData.name, i)
                        : this.formData.name;
                try {
                    await this.handleExperimentSetupSubmission(experimentName);
                } catch (err) {
                    console.log(err);
                    let errorMessage = err.message;
                    EventBus.$emit("globalLoadScreen", { status: false, message: null });
                    if (this.formData.numberOfExperiments - i > 1) {
                        errorMessage += `\nThe additional ${this.formData.numberOfExperiments - i - 1} requested experiment(s) will not be created.`;
                    }
                    EventBus.$emit("globalAlert", errorMessage);
                }
            }
        },
        async handleExperimentSetupSubmission(experimentName) {
            // Prepare file and form data

            const formData = new FormData();
            formData.append("file", this.file);
            const formJson = JSON.stringify({ ...this.formData, name: experimentName });
            const blob = new Blob([formJson], { type: "application/json" });
            formData.append("data", blob);

            let setupResponse;
            try {
                // Submit experiment setup
                setupResponse = await axios.post(`/experiment/setup`, formData);
            } catch (err) {
                const errorMessageFromServer = _.get(err, "response.data.error.message");
                if (errorMessageFromServer) {
                    throw new ExperimentSetupError(
                        `${errorMessageFromServer}\nMore details are available in your browser console.`
                    );
                } else {
                    console.log(err);
                    throw new ExperimentSetupError(
                        `An error occurred. ${err.message}.\nMore details are available in your browser console.`
                    );
                }
            }

            const experiment = setupResponse.data;
            if (experiment.errors) {
                // TODO: is it errorMessage or errorText?
                console.log(`${experiment.errorMessage}:`, experiment.errors);
                throw new ExperimentSetupError(experiment.errorText);
            } else {
                const experimentId = experiment.displayId;
                console.log(`New experiment ${experimentId} setup submitted`, experiment);

                const createdExperiment = {
                    experimentId: experimentId,
                    webURL: experiment.webURL,
                    benchlingId: experiment.id,
                    setupStatus: "Pending",
                    setupErrors: [],
                };

                this.createdExperiments.push(createdExperiment);

                EventBus.$emit("globalLoadScreen", { status: false, message: null });
                try {
                    await this.pollStatus(createdExperiment.experimentId);
                } catch (err) {
                    const errorMessageFromServer = _.get(err, "response.data.error.message");
                    if (errorMessageFromServer) {
                        throw new ExperimentSetupError(
                            `${errorMessageFromServer}\nMore details are available in your browser console.`
                        );
                    } else {
                        console.log(err);
                        throw new ExperimentSetupError(
                            `An error occurred. ${err.message}.\nMore details are available in your browser console.`
                        );
                    }
                }
                if (createdExperiment.setupStatus === "Failed") {
                    throw new ExperimentSetupError(
                        `Experiment setup background tasks failed with the following error(s): ${createdExperiment.setupStatus.errors.join(", ")}`
                    );
                }
            }
        },
        constructExperimentName(baseName, index) {
            // if the name ends with -###, increment ### by the index; otherwise, append the index + 1 in parentheses
            // sample baseName = NA-24-GRP-OP-012-001
            // desired name for second experiment = NA-24-GRP-OP-012-002
            const match = baseName.match(/^(.*?)(-\d+)$/);
            if (match) {
                const experimentSuffix = parseInt(match[2].slice(1)) + index;
                const paddedSuffix = experimentSuffix.toString().padStart(match[2].length - 1, "0");
                return `${match[1]}-${paddedSuffix}`;
            } else {
                return `${baseName} (${index + 1})`;
            }
        },
        async validate() {
            const setupForm = this.$refs.setupForm;
            const validationResult = await setupForm.validate();
            if (!validationResult.valid) {
                const errors = validationResult.errors;
                const msgs = errors.map((error) => {
                    return error.errorMessages.join(", ");
                });
                EventBus.$emit("globalAlert", `There are validation errors: ${msgs.join(", ")}`);
                return false;
            } else {
                return true;
            }
        },
        pollStatus: async function (experimentId) {
            const createdExperiment = this.createdExperiments.find(
                (experiment) => experiment.experimentId === experimentId
            );
            createdExperiment.setupStatus = "Pending";
            createdExperiment.setupErrors = [];
            let count = 0;
            while (createdExperiment.setupStatus === "Pending" && count < 100) {
                await new Promise((resolve) => setTimeout(resolve, 5000));
                const expStatus = await axios.get(`/experiment/setup/status/${experimentId}`);
                console.log("Experiment Status Response:", expStatus.data);
                createdExperiment.setupStatus = expStatus.data.status;
                createdExperiment.setupErrors = expStatus.data.errors;
                count++;
            }
        },
        confluenceSetupExited: function () {
            this.useConfluenceExperimentSetup = false;
        },
        async setExperimentMetadata(experimentGroupId) {
            if (!experimentGroupId) {
                this.experimentMetadata = {};
                return;
            }
            try {
                const response = await experimentGroupsAPI.fetchExperimentGroup(experimentGroupId);
                if (response.isConfluenceExperimentSetupRequired) {
                    EventBus.$emit(
                        "globalAlert",
                        `${response.name} (${response.code}) requires Confluence experiment setup.`
                    );
                    this.useConfluenceExperimentSetup = true;
                } else {
                    this.experimentMetadata = response.experimentMetadata || {};
                }
            } catch (error) {
                this.experimentMetadata = {};
            }
        },
    },

    watch: {
        // if the selectedExperimentGroupId changes, fetch the experimentGroup from the backend
        // to populate experimentMetadata with the group's defaults
        selectedExperimentGroupId: {
            async handler(newValue, oldValue) {
                if (!oldValue || newValue !== oldValue) {
                    await this.setExperimentMetadata(newValue);
                }
            },
            deep: true,
            immediate: true,
        },
        objective: {
            handler(newValue, oldValue) {
                if (oldValue !== newValue) {
                    this.selectedExperimentGroupId = null;
                }
            },
            immediate: true,
        },
    },

    data() {
        return {
            selectedProjectFolder: null,
            selectedProtocol: null,
            selectedExperimentGroupId: null,
            file: null,
            protocols: [],
            menu: null,
            containerTypes: [],
            experimentGroups: [],
            formData: {
                name: null,
                folderId: null,
                assay_type: null,
                start_date: null,
                container_type: null,
                projectId: null,
                fileData: null,
                numberOfExperiments: 1,
                use_measurements: true,
                experimentGroupId: null,
            },
            isFormValid: null,
            requiredTextValueRules: requiredTextValueRules,
            requiredSimpleValueRules: requiredSimpleValueRules,
            requiredFileRules: requiredFileRules,
            experimentMetadata: {},
            createdExperiments: [],
            saving: false,
            useConfluenceExperimentSetup: false,
            experimentObjectives: [],
            objective: null,
        };
    },
});
</script>
